// Billing view of a host (PR-989 decision 68, Q4): billable while it reported
// within the last 30 days and is not archived; flagged from the seventh silent
// day; out of the billable count at 30; archived never bills. Mirrors
// server/src/services/BillingRosterService.ts billingStateFor(). The server
// sends silentDays and billingState on every agent since Server #146; the
// fallback below computes the same thing from lastSeenAt for older payloads.

export type BillingState = 'billable' | 'silent_warning' | 'stale' | 'never_seen' | 'archived';

export const SILENT_WARNING_DAYS = 7;
export const BILLABLE_WINDOW_DAYS = 30;

export interface BillingAgentLike {
  lastSeenAt?: string | Date | null;
  isArchived?: boolean;
  archivedAt?: string | null;
  billingState?: BillingState | string | null;
  silentDays?: number | null;
}

export function silentDaysSince(lastSeenAt: string | Date | null | undefined, now: Date = new Date()): number | null {
  if (!lastSeenAt) return null;
  const seen = lastSeenAt instanceof Date ? lastSeenAt : new Date(lastSeenAt);
  if (Number.isNaN(seen.getTime())) return null;
  return Math.max(0, Math.floor((now.getTime() - seen.getTime()) / 86_400_000));
}

export function deriveBillingState(agent: BillingAgentLike, now: Date = new Date()): { state: BillingState; silentDays: number | null } {
  const known = agent.billingState as BillingState | undefined;
  if (known && ['billable', 'silent_warning', 'stale', 'never_seen', 'archived'].includes(known)) {
    return { state: known, silentDays: agent.silentDays ?? silentDaysSince(agent.lastSeenAt, now) };
  }
  const archived = agent.isArchived === true || !!agent.archivedAt;
  const silentDays = silentDaysSince(agent.lastSeenAt, now);
  if (archived) return { state: 'archived', silentDays };
  if (silentDays === null) return { state: 'never_seen', silentDays };
  if (silentDays >= BILLABLE_WINDOW_DAYS) return { state: 'stale', silentDays };
  if (silentDays >= SILENT_WARNING_DAYS) return { state: 'silent_warning', silentDays };
  return { state: 'billable', silentDays };
}

export function billingStateLabel(state: BillingState, silentDays: number | null): string {
  switch (state) {
    case 'billable': return 'Billable';
    case 'silent_warning': return silentDays === null ? 'Silent' : `Silent ${silentDays} ${silentDays === 1 ? 'day' : 'days'}`;
    case 'stale': return silentDays === null ? 'Stale, not billed' : `Stale ${silentDays} days, not billed`;
    case 'never_seen': return 'Never seen, not billed';
    case 'archived': return 'Archived, not billed';
  }
}

/** Short explanation for a tooltip or aside. */
export function billingStateHelp(state: BillingState): string {
  switch (state) {
    case 'billable': return 'Reported within the last 30 days. Billed for today.';
    case 'silent_warning': return `No report for ${SILENT_WARNING_DAYS} or more days. Still billed until day ${BILLABLE_WINDOW_DAYS}. Archive it to stop billing today.`;
    case 'stale': return `No report for ${BILLABLE_WINDOW_DAYS} or more days. Out of the billable count.`;
    case 'never_seen': return 'Registered but has never reported. Not billed.';
    case 'archived': return 'Archived. Not billed since the day it was archived.';
  }
}

/** Tailwind classes for the pill, light-theme table variant used by the fleet list. */
export function billingStatePillClass(state: BillingState): string {
  switch (state) {
    case 'billable': return 'text-gray-600 bg-gray-100';
    case 'silent_warning': return 'text-amber-800 bg-amber-100 border border-amber-300';
    case 'stale': return 'text-gray-500 bg-gray-50 border border-gray-200';
    case 'never_seen': return 'text-gray-500 bg-gray-50 border border-gray-200';
    case 'archived': return 'text-gray-400 bg-gray-50 border border-dashed border-gray-300';
  }
}

export type BillingStateCounts = Record<BillingState, number>;

export function summarizeBillingStates(agents: BillingAgentLike[], now: Date = new Date()): BillingStateCounts {
  const counts: BillingStateCounts = { billable: 0, silent_warning: 0, stale: 0, never_seen: 0, archived: 0 };
  for (const a of agents) counts[deriveBillingState(a, now).state] += 1;
  return counts;
}

/** Money helper for the billing views: integer cents to a USD string. */
export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '--';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** First day of the month for an ISO date (UTC), as YYYY-MM-DD. */
export function monthStartOf(iso: string): string {
  return iso.slice(0, 8) + '01';
}

export function todayIso(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Build a CSV string from rows; quotes fields that need it. RFC 4180 line endings. */
export function toCsv(header: string[], rows: Array<Array<string | number | null | undefined>>): string {
  const cell = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [header, ...rows].map(r => r.map(cell).join(',')).join('\r\n') + '\r\n';
}
